import {
  AgentChat,
  getAgentAccessibleName,
  getAgentDisplayName,
  ProposalsHeader,
  resolveAgentVisuals,
  useAgentChildren,
} from '@tunasong/plugin-agent/integration'
import { logger } from '@tunasong/models'
import { features, useSelector, useThunkDispatch } from '@tunasong/redux'
import type { ColorName } from '@tunasong/schemas'
import { getIconColorCSS, Tab, Tabs, Typography, type SidebarComponent } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, SyntheticEvent } from 'react'
import { useEffect } from 'react'
import { AgentChatEmptyHeader } from './agent-chat-empty-header.js'
import { AgentIdentityHeader } from './agent-identity-header.js'
import styles from './sidebar-agents.module.css'

const CHAT_BOX_ID = 'agent-chat-input'

/**
 * Body of the sidebar's `Agents` tool.
 *
 * Lists every agent attached to the host entity (one tab per agent for 2+,
 * single agent for 1) and renders the active agent's chat thread with the
 * `Proposals (n)` strip sticky on top. Choosing an agent from the header's
 * Agents pill sets `ui.sidebar.activeAgentId`, which selects its thread here.
 *
 * `AgentIdentityHeader` renders on every thread state (identity + roleLine
 * + the permanent aria-describedby anchor). Below it, `AgentChatEmptyHeader`
 * self-hides once the active agent has any comments — so a fresh agent panel
 * opens with clickable starter prompts rather than a blank surface.
 *
 * Empty state covers the cold-cache window where `useAgentChildren` returns
 * `[]` before the children query resolves; the surrounding `disabled`
 * predicate in `sidebar.tsx` filters out entity types that can never host
 * agents, but children-not-yet-loaded leaks through.
 */
export const SidebarAgents: FC<SidebarComponent> = ({ entity, className }) => {
  const dispatch = useThunkDispatch()
  const agents = useAgentChildren(entity?.id)
  const activeAgentId = useSelector(state => state.ui.sidebar.activeAgentId)

  // Discard an intent aimed at an agent that is not (or no longer) attached —
  // e.g. deleted between dispatch and render. Without this the intent lingers
  // until the next tool switch. Guarded on agents.length so the cold-cache
  // window (children not loaded yet) doesn't false-positive.
  const composerIntent = useSelector(state => state.ui.sidebar.composerIntent)
  useEffect(() => {
    if (!composerIntent || agents.length === 0) {
      return
    }
    if (!agents.some(a => a.id === composerIntent.agentId)) {
      logger.warn('sidebar-agents: discarding composer intent for unknown agent', { agentId: composerIntent.agentId })
      dispatch(features.ui.actions.clearComposerIntent())
    }
  }, [composerIntent, agents, dispatch])

  if (agents.length === 0) {
    return (
      <Typography variant="caption" className={cn(styles.empty, className)}>
        No agents attached to this entity.
      </Typography>
    )
  }

  // Resolve the active agent: Redux selection wins, otherwise the first
  // attached agent (so a fresh open lands on a real thread instead of
  // empty).
  const activeAgent = agents.find(a => a.id === activeAgentId) ?? agents[0]

  const handleTab = (_event: SyntheticEvent | null, value: string | number) => {
    const next = agents.find(a => a.id === value)
    if (next) {
      dispatch(features.ui.actions.setSidebarActiveAgentId(next.id))
    }
  }

  const handleSelectPrompt = (prompt: string) => {
    dispatch(features.ui.actions.openAgentComposer({ agentId: activeAgent.id, draft: prompt }))
  }

  return (
    <div className={cn(styles.container, className)} role="region" aria-label="Agent chat">
      {agents.length > 1 ? (
        <Tabs value={activeAgent.id} onChange={handleTab} className={styles.tabs}>
          {agents.map(agent => {
            const { color, Icon } = resolveAgentVisuals(agent)
            const iconColor = getIconColorCSS(color as ColorName)
            return (
              <Tab
                key={agent.id}
                value={agent.id}
                label={getAgentDisplayName(agent)}
                // A band-chosen name is spoken with what the agent is for: "Nils, Chord Advisor" (Joni §4)
                aria-label={getAgentAccessibleName(agent)}
                icon={<Icon className={styles.tabIcon} style={{ color: iconColor }} aria-hidden="true" />}
                iconPosition="start"
              />
            )
          })}
        </Tabs>
      ) : null}
      <div className={styles.body}>
        <AgentIdentityHeader agent={activeAgent} composerId={CHAT_BOX_ID} showName={agents.length === 1} />
        <ProposalsHeader agentId={activeAgent.id} />
        <AgentChatEmptyHeader agent={activeAgent} onSelectPrompt={handleSelectPrompt} />
        <AgentChat element={activeAgent} showTabs={false} composerId={CHAT_BOX_ID} />
      </div>
    </div>
  )
}
